import type { OnChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type { RequestError } from '@freelancer/datastore';
import type { BackendErrorResponse } from '@freelancer/freelancer-http';
import { ErrorTracking } from '@freelancer/tracking';
import { FontColor, FontType, FontWeight } from '@freelancer/ui/text';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { GenericErrorAlign, exceptionErrors } from './generic-error.types';

@Component({
  selector: 'fl-generic-error',
  template: `
    <div
      class="ErrorContainer"
      [attr.data-align]="align"
    >
      <fl-text
        i18n="Generic error message title"
        [color]="FontColor.FOREGROUND"
        [fontType]="multiLine ? FontType.PARAGRAPH : FontType.SPAN"
        [flMarginBottom]="multiLine ? 'xxsmall' : 'none'"
      >
        Oops, something went wrong.
        <!-- supplied message, eg. "could not place your bid" -->
        <ng-content></ng-content>
        <ng-container *ngIf="isRateLimit">
          You've sent too many requests.
        </ng-container>
      </fl-text>
      <fl-text
        i18n="Generic error solution suggestions text"
        [color]="FontColor.FOREGROUND"
        [fontType]="multiLine ? FontType.PARAGRAPH : FontType.SPAN"
        [flMarginBottom]="multiLine ? 'xxsmall' : 'none'"
      >
        Please
        <fl-link
          *ngIf="canRetry || hasSetRetry; else noRetry"
          flTrackingLabel="LinkToRetryGenericError"
          (click)="handleRetry($event)"
        >
          try again.
        </fl-link>
        <ng-template #noRetry>
          <ng-container *ngIf="isRateLimit; else noRetryNoRatelimit">
            wait and try again later.
          </ng-container>
          <ng-template #noRetryNoRatelimit>try again.</ng-template>
        </ng-template>
      </fl-text>
      <fl-text
        i18n="Default redirect to support suggestion"
        [color]="FontColor.FOREGROUND"
        [fontType]="multiLine ? FontType.PARAGRAPH : FontType.SPAN"
        [flMarginBottom]="multiLine ? 'xxsmall' : 'none'"
      >
        If the problem persists, please contact
        <fl-link
          flTrackingLabel="LinkToSupportGenericError"
          [link]="'/support'"
        >
          support
        </fl-link>
        with the following error information:
      </fl-text>
    </div>
    <div
      class="ErrorContainer"
      [attr.data-align]="align"
    >
      <fl-text
        i18n="Generic error message error code"
        [color]="FontColor.FOREGROUND"
        [fontType]="multiLine ? FontType.PARAGRAPH : FontType.SPAN"
      >
        Error Code: {{ error.errorCode }}
      </fl-text>
      <fl-text
        *ngIf="error.requestId"
        i18n="Generic error message request ID"
        [color]="FontColor.FOREGROUND"
        [fontType]="multiLine ? FontType.PARAGRAPH : FontType.SPAN"
      >
        Request ID: {{ error.requestId }}
      </fl-text>
    </div>
  `,
  styleUrls: ['./generic-error.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericErrorComponent implements OnChanges {
  FontColor = FontColor;
  FontType = FontType;
  FontWeight = FontWeight;
  GenericErrorAlign = GenericErrorAlign;

  isRateLimit = false;

  canRetry = false;
  hasSetRetry = false;

  hasGenericErrorCode = false;

  @Input() align = GenericErrorAlign.LEFT;
  @Input() error: BackendErrorResponse<any> | RequestError<any>;
  @Input() multiLine = false;

  @Output() retry = new EventEmitter<MouseEvent>();

  constructor(private errorTracking: ErrorTracking) {}

  ngOnChanges(): void {
    this.isRateLimit = this.error.errorCode === ErrorCodeApi.TOO_MANY_REQUESTS;
    this.canRetry = 'retry' in this.error && !this.isRateLimit;
    this.hasSetRetry = this.retry.observed && !this.isRateLimit;
    this.hasGenericErrorCode = exceptionErrors.includes(this.error.errorCode);

    if (!this.hasGenericErrorCode) {
      this.errorTracking.captureMessage(
        `fl-generic-error has non generic error: ${this.error.errorCode}`,
        {
          extra: {
            request_id: this.error.requestId,
          },
        },
      );
    }
  }

  handleRetry(e: MouseEvent): void {
    if (this.hasSetRetry) {
      this.retry.emit(e);
    } else if (this.canRetry) {
      (this.error as RequestError<any>).retry();
    }
  }
}
